import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Clock, Home, Gift, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GiftCardSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking');
  const [giftCard, setGiftCard] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/gift-cards/checkout/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          setGiftCard(data.gift_card);
          // Google Ads — Chargement de page : conversion "Achat" confirmée
          // Se déclenche uniquement sur la page de confirmation après paiement Stripe réel
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'ads_conversion_Achat_1', {
              'value': data.gift_card?.amount || 0,      // Montant réel de la carte cadeau en €
              'currency': 'EUR',
              'transaction_id': data.gift_card?.code || sessionId // Évite les doublons
            });
          }
        } else if (data.status === 'expired') {
          setStatus('expired');
        } else if (pollCount < 10) {
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (pollCount < 5) {
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, pollCount]);

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <div className="text-center" data-testid="status-checking">
            <div className="w-20 h-20 mx-auto mb-6 bg-gold/20 rounded-full flex items-center justify-center">
              <Clock className="text-gold animate-pulse" size={40} />
            </div>
            <h1 className="font-heading text-3xl text-warm-brown mb-4">
              Vérification du paiement...
            </h1>
            <p className="font-body text-warm-brown-light">
              Veuillez patienter pendant que nous confirmons votre paiement.
            </p>
            <div className="mt-8">
              <div className="spinner mx-auto"></div>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="text-center" data-testid="status-success">
            <div className="w-20 h-20 mx-auto mb-6 bg-sage-100 rounded-full flex items-center justify-center">
              <CheckCircle className="text-sage" size={40} />
            </div>
            <h1 className="font-heading text-3xl text-warm-brown mb-4">
              Carte cadeau envoyée !
            </h1>
            <p className="font-body text-warm-brown-light mb-8">
              La carte cadeau a été envoyée par email à <strong>{giftCard?.recipient_email}</strong>
            </p>
            
            {giftCard && (
              <div className="bg-sand rounded-2xl p-6 mb-8 max-w-md mx-auto">
                <p className="font-body text-warm-brown mb-2">
                  Code : <strong className="text-terracotta font-mono">{giftCard.code}</strong>
                </p>
                <p className="font-body text-warm-brown mb-2">
                  Montant : <strong className="text-terracotta">{giftCard.amount} €</strong>
                </p>
                <p className="font-body text-sm text-warm-brown-light">
                  Valide jusqu'au {new Date(giftCard.expires_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {giftCard?.code && (
                <a
                  href={`${API}/gift-cards/${giftCard.code}/pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={`Carte-Cadeau-Hilanta-${giftCard.code}.pdf`}
                  data-testid="download-gift-card-pdf"
                >
                  <Button
                    className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-6 py-3 font-body"
                  >
                    <Download className="mr-2" size={18} />
                    Télécharger la carte (PDF)
                  </Button>
                </a>
              )}
              <Link to="/">
                <Button 
                  variant="outline"
                  className="border-2 border-terracotta text-terracotta hover:bg-terracotta hover:text-white rounded-full px-6 py-3 font-body"
                  data-testid="back-home"
                >
                  <Home className="mr-2" size={18} />
                  Retour à l'accueil
                </Button>
              </Link>
              <Link to="/carte-cadeau">
                <Button 
                  className="bg-terracotta hover:bg-terracotta-600 text-white rounded-full px-6 py-3 font-body"
                  data-testid="new-gift-card"
                >
                  <Gift className="mr-2" size={18} />
                  Offrir une autre carte
                </Button>
              </Link>
            </div>
          </div>
        );

      case 'pending':
        return (
          <div className="text-center" data-testid="status-pending">
            <div className="w-20 h-20 mx-auto mb-6 bg-gold/20 rounded-full flex items-center justify-center">
              <Clock className="text-gold" size={40} />
            </div>
            <h1 className="font-heading text-3xl text-warm-brown mb-4">
              Paiement en cours
            </h1>
            <p className="font-body text-warm-brown-light mb-8">
              Votre paiement est en cours de vérification. La carte cadeau sera envoyée dès confirmation.
            </p>
            <Link to="/">
              <Button className="bg-terracotta hover:bg-terracotta-600 text-white rounded-full px-8 py-4 font-body">
                <Home className="mr-2" size={18} />
                Retour à l'accueil
              </Button>
            </Link>
          </div>
        );

      case 'expired':
      case 'error':
      default:
        return (
          <div className="text-center" data-testid="status-error">
            <div className="w-20 h-20 mx-auto mb-6 bg-terracotta-100 rounded-full flex items-center justify-center">
              <XCircle className="text-terracotta" size={40} />
            </div>
            <h1 className="font-heading text-3xl text-warm-brown mb-4">
              Une erreur est survenue
            </h1>
            <p className="font-body text-warm-brown-light mb-8">
              Nous n'avons pas pu traiter votre carte cadeau. Veuillez réessayer.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-terracotta text-terracotta hover:bg-terracotta hover:text-white rounded-full px-6 py-3 font-body">
                  Nous contacter
                </Button>
              </Link>
              <Link to="/carte-cadeau">
                <Button className="bg-terracotta hover:bg-terracotta-600 text-white rounded-full px-6 py-3 font-body">
                  Réessayer
                </Button>
              </Link>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen py-20" data-testid="gift-card-success-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl shadow-lg p-8 md:p-12">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default GiftCardSuccess;
